import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { combineLatest, map, Observable, of, startWith } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { Actions } from '@ngrx/effects';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { IVariant } from '../../../models/products.models';

@Component({
  selector: 'app-edit-variant-product-infos',
  templateUrl: './edit-variant-product-infos.component.html',
  styleUrls: ['./edit-variant-product-infos.component.scss']
})
export class EditVariantProductInfosComponent implements OnInit {

  form!: FormGroup;
  variantsForm!: FormGroup;
  brands$: Observable<IBrand[]> = of([]);
  filteredBrands$!: Observable<IBrand[]>;
  brand_id!: number;
  brand: IBrand | null = null;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  constructor(
    public dialogRef: MatDialogRef<EditVariantProductInfosComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private actions$: Actions
  ) {
    this.brands$ = this.store.select((state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name)));
    this.variantsForm = this.fb.group({});
   }
   ngOnInit(): void {
    this.form = this.fb.group({
      name: new FormControl(this.data?.name ?? '', Validators.required),
      brandControl: new FormControl(this.data?.brand?.name ?? ''),
      description: new FormControl(this.data?.description),
      status: new FormControl(this.data?.status ?? ''),
      expiry_date: new FormControl(this.data?.expiry_date ?? ''),
      hasExpirationDate: new FormControl(this.data?.expiry_date ? true : false)
    });

    this.data.variants.forEach((variant: IVariant) => {
      this.variantsForm.addControl(`sku_${variant.id}`, new FormControl(variant.sku ?? '', [Validators.required]));
      this.variantsForm.addControl(`barcode_${variant.id}`, new FormControl(variant.barcode ?? '', []));
      this.variantsForm.addControl(`enabled_${variant.id}`, new FormControl(variant.enabled ?? true, []));
      this.variantsForm.addControl(`extra_identifiers_${variant.id}`, new FormControl(variant.extra_identifiers ?? [], []));
    });

    this.brand_id = this.data.brand_id;
    this.brand = this.data.brand;

    this.form.get('brandControl')?.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.trim() === '') {
        this.brand_id = 0
        this.brand = null
        this.form.get('brandControl')?.setValue(null, { emitEvent: false });
      }
    });

    if (this.data?.description) {
      const cleanDescription = this.stripHtml(this.data.description);
      this.form.patchValue({
        description: cleanDescription
      });
    }

    this.brand_id = this.data.brand_id;
    this.brand = this.data.brand;

    this.filteredBrands$ = combineLatest([
      this.brands$,
      this.form.get('brandControl')!.valueChanges.pipe(startWith('')),
    ]).pipe(map(([brands, input]) => this._filterStrings(brands, input)));
  }

  private _filterStrings(strings: any[], value: string): any[] {
    const filterValue = this._normalizeValue(value);
    return strings.filter((string) =>
      this._normalizeValue(string.name)?.includes(filterValue!)
    );
  }
  private _normalizeValue(value: string | undefined): string | undefined {
    return value?.toLowerCase().replace(/\s/g, '');
  }

  private stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').trim();
  }

  onBrandSelected(brand: IBrand) {
    if (brand) {
      this.brand_id = brand.id;
      this.brand = brand;
    }
  }

  toggleExpirationDate(event: any) {
    const checkbox = event.checked
    
    if (checkbox) {
      this.form.get('hasExpirationDate')?.setValue(true);
      this.form.get('expiry_date')?.setValue(this.data?.expiry_date ?? '');
      this.form
        .get('expiry_date')
        ?.setValidators([Validators.required]);
    } else {
      this.form.get('hasExpirationDate')?.setValue(false);
      this.form.get('expiry_date')?.clearValidators();
      this.form.get('expiry_date')?.setValue('');
      // Reset the touched and pristine states to hide validation messages
      this.form.get('expiry_date')?.markAsUntouched();
      this.form.get('expiry_date')?.markAsPristine();
    }
    this.form.get('expiry_date')?.updateValueAndValidity();
    this.form.get('hasExpirationDate')?.updateValueAndValidity();
  }

  save(): boolean | void{
    if (!this.variantsForm.valid) {
      return false;
    }
    const result = {
      product: {
        ...this.form.value,
        brandControl: undefined,
        hasExpirationDate: undefined,
        brand_id: this.brand_id,
      },
      variants: this.data.variants.map((variant: IVariant) => {
        return {
          ...variant,
          sku: this.variantsForm.get(`sku_${variant.id}`)?.value,
          barcode: this.variantsForm.get(`barcode_${variant.id}`)?.value,
          extra_identifiers: this.variantsForm.get(`extra_identifiers_${variant.id}`)?.value,
          enabled: this.variantsForm.get(`enabled_${variant.id}`)?.value,
          image: undefined,
          values: undefined,
          code_identifiers: undefined,
          codes: undefined,
        }
      })
    }
    this.dialogRef.close(result);

  }

  cancel(){
    this.dialogRef.close();
  }

  getExtraIdentifiers(variantId: number): string[] {
    const control = this.variantsForm.get(`extra_identifiers_${variantId}`);
    return control?.value || [];
  }

  addExtraIdentifier(variant: IVariant, event: any): void {
    const input = event.input;
    const value = event.value;

    // Add our identifier
    if ((value || '').trim()) {
      const currentIdentifiers = this.getExtraIdentifiers(variant.id);
      const trimmedValue = value.trim();
      
      // Check if identifier already exists
      if (!currentIdentifiers.includes(trimmedValue)) {
        const updatedIdentifiers = [...currentIdentifiers, trimmedValue];
        this.variantsForm.get(`extra_identifiers_${variant.id}`)?.setValue(updatedIdentifiers);
      }
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  removeExtraIdentifier(variant: IVariant, identifier: string): void {
    const currentIdentifiers = this.getExtraIdentifiers(variant.id);
    const updatedIdentifiers = currentIdentifiers.filter(id => id !== identifier);
    this.variantsForm.get(`extra_identifiers_${variant.id}`)?.setValue(updatedIdentifiers);
  }
}
